import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Calendar, Inbox, X } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '@/lib/dateFnsLocale';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchPastWebinarsData } from '../store/analyticsSlice';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';

interface WebinarSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (webinar: any) => void;
  excludeId?: number | null;
}

export const WebinarSelectionModal: React.FC<WebinarSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  excludeId,
}) => {
  const dispatch = useAppDispatch();
  const currentWorkspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);

  const { list: webinars, loading } = useAppSelector((state) => state.analytics.pastWebinars);
  const totalPages = useAppSelector((state: any) => state.analytics.pastWebinars.totalPages || 0);

  const { t, i18n } = useTranslation('analytics');
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 5;

  useEffect(() => {
    if (isOpen && currentWorkspaceId) {
      dispatch(
        fetchPastWebinarsData({
          workspaceId: currentWorkspaceId,
          page: currentPage,
          size: pageSize,
        }),
      );
    }
  }, [isOpen, currentWorkspaceId, currentPage, dispatch]);

  const handleSelect = (webinar: any) => {
    onSelect(webinar);
    onClose();
  };

  // Filtrage local pour l'exclusion
  const filteredWebinars = webinars.filter((w: any) => w.sessionId !== excludeId);

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-[32px] bg-white p-8 text-left shadow-2xl transition-all border border-neutral-100">
                <div className="flex items-center justify-between mb-8">
                  <Dialog.Title className="text-2xl font-bold text-dark">
                    {t('modal.title')}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
                  >
                    <X size={20} className="text-secondary" />
                  </button>
                </div>

                <div className="space-y-4">
                  {loading ? (
                    <div className="py-20 flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
                    </div>
                  ) : filteredWebinars.length === 0 ? (
                    /* GESTION DE LA LISTE VIDE */
                    <div className="py-16 flex flex-col items-center justify-center text-center">
                      <div className="bg-neutral-50 p-4 rounded-full mb-4">
                        <Inbox size={40} className="text-neutral-300" />
                      </div>
                      <h3 className="text-lg font-semibold text-dark mb-1">
                        {t('modal.empty.title')}
                      </h3>
                      <p className="text-secondary text-sm max-w-xs">{t('modal.empty.sub')}</p>
                    </div>
                  ) : (
                    <>
                      <div className="max-h-[400px] overflow-y-auto pr-2 space-y-3">
                        {filteredWebinars.map((webinar: any) => (
                          <div
                            key={webinar.sessionId}
                            onClick={() => handleSelect(webinar)}
                            className="group flex items-center justify-between p-4 rounded-2xl border border-neutral-100 hover:border-accent/50 hover:bg-accent/5 cursor-pointer transition-all"
                          >
                            <div className="flex flex-col">
                              <span className="font-semibold text-dark group-hover:text-accent transition-colors">
                                {webinar.title}
                              </span>
                              <div className="flex items-center text-xs text-secondary mt-1">
                                <Calendar size={12} className="mr-1" />
                                {format(new Date(webinar.projectionDate), 'd MMMM yyyy', {
                                  locale: dateFnsLocale,
                                })}
                              </div>
                            </div>
                            <Badge
                              variant="outline"
                              className="bg-neutral-50 uppercase text-[10px]"
                            >
                              {webinar.sessionType}
                            </Badge>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 pt-4 border-t border-neutral-50">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
